import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  initialColumns,
  initialIssues,
} from "../../../../redux/store/mockData";

export interface Comment {
  id: string;
  text: string;
  createdAt?: string;
  author: string;
  at?: string;
}

export interface Issue {
  id: string;
  key: string;
  title: string;
  description: string;
  status: string;
  priority?: string;
  assignee?: string;
  reporter?: string;
  category?: string;
  createdAt?: string;
  updatedAt: string;
  dueDate?: string;
  estimate?: number;
  tags?: string[];
  comments?: Comment[];
  [key: string]: any;
}

export interface Column {
  id: string;
  name: string;
  visible: boolean;
}

export interface Filters {
  search: string;
  status: string;
  priority: string;
  assignee: string;
  category: string;
  sla: string;
  view: string;
  sortBy: string;
}

interface BoardState {
  columns: Column[];
  issues: Issue[];
  filters: Filters;
  boardView: string;
}

const initialState: BoardState = {
  columns: initialColumns,
  issues: initialIssues,
  boardView: "list",
  filters: {
    search: "",
    status: "all",
    priority: "all",
    assignee: "all",
    category: "all",
    sla: "all",
    view: "list",
    sortBy: "none",
  },
};

const boardReducer = createSlice({
  name: "board",
  initialState,
  reducers: {
    // setColumns: (state, action: PayloadAction<Column[]>) => {
    //   state.columns = action.payload;
    // },
    setFilters: (state, action: PayloadAction<Partial<Filters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    setBoardView: (state, action: any) => {
      state.boardView = action.payload;
    },
    addIssue: (state, action: PayloadAction<Issue>) => {
      state.issues.unshift(action.payload);
    },
    updateIssue: (
      state,
      action: PayloadAction<{ id: string; patch: Partial<Issue> }>
    ) => {
      const issue = state.issues.find((i) => i.id === action.payload.id);
      if (issue) {
        Object.assign(issue, action.payload.patch);
        issue.updatedAt = new Date().toISOString();
      }
    },
    addComment: (
      state,
      action: PayloadAction<{ issueId: string; comment: Comment }>
    ) => {
      const issue = state.issues.find((i) => i.id === action.payload.issueId);
      if (issue) {
        issue.comments = [action.payload.comment, ...(issue.comments || [])];
      }
    },
    moveIssueWithinColumn: (
      state,
      action: PayloadAction<{
        columnId: string;
        fromIndex: number;
        toIndex: number;
      }>
    ) => {
      const { columnId, fromIndex, toIndex } = action.payload;
      const issuesInColumn = state.issues.filter((i) => i.status === columnId);

      if (
        fromIndex < 0 ||
        fromIndex >= issuesInColumn.length ||
        toIndex < 0 ||
        toIndex >= issuesInColumn.length
      )
        return;

      const [movedIssue] = issuesInColumn.splice(fromIndex, 1);
      issuesInColumn.splice(toIndex, 0, movedIssue);

      const otherIssues = state.issues.filter((i) => i.status !== columnId);
      state.issues = [...otherIssues, ...issuesInColumn];
    },
    moveIssueAcrossColumns: (
      state,
      action: PayloadAction<{
        fromColumnId: string;
        fromIndex: number;
        toColumnId: string;
        toIndex: number;
      }>
    ) => {
      const { fromColumnId, fromIndex, toColumnId, toIndex } = action.payload;
      const fromIssues = state.issues.filter((i) => i.status === fromColumnId);
      const toIssues = state.issues.filter((i) => i.status === toColumnId);

      if (
        fromIndex < 0 ||
        fromIndex >= fromIssues.length ||
        toIndex < 0 ||
        toIndex > toIssues.length // allow toIndex === toIssues.length for appending
      )
        return;

      const [movedIssue] = fromIssues.splice(fromIndex, 1);
      movedIssue.status = toColumnId;
      movedIssue.updatedAt = new Date().toISOString();

      toIssues.splice(toIndex, 0, movedIssue);

      const otherIssues = state.issues.filter(
        (i) => i.status !== fromColumnId && i.status !== toColumnId
      );

      state.issues = [...otherIssues, ...fromIssues, ...toIssues];
    },
  },
});

export const {
  // setColumns,
  setFilters,
  addIssue,
  updateIssue,
  addComment,
  moveIssueWithinColumn,
  moveIssueAcrossColumns,
  setBoardView,
} = boardReducer.actions;

export default boardReducer.reducer;
